import * as THREE from 'three';
import { Ball } from '@deepwake/shared';
import { ClientSim, BallDeco } from './sim.js';
import { buildShip, buildStation, buildGate, buildBeltBeacon, buildSun, buildPlanet, buildWreck, buildAsteroid } from './models.js';
import { paintSkybox } from './textures.js';
import { Vfx, WarpTunnel } from './vfx.js';

/**
 * Floating-origin renderer: everything is positioned relative to the camera
 * focus (your ship), and far celestials collapse onto an impostor sphere so
 * float32 never sees AU-sized coordinates.
 */
const IMPOSTOR_RANGE = 4.0e6; // beyond this, draw direction-clamped

export class Renderer {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  objects = new Map<
    number,
    {
      group: THREE.Group;
      kind: string;
      builtFor?: string;
      blinkers: THREE.Mesh[];
      spinners: THREE.Object3D[];
      gateDisc: THREE.Mesh | null;
      engines: THREE.Mesh[];
    }
  >();

  // Camera orbit state
  camYaw = 0.6;
  camPitch = 0.35;
  camDist = 220;
  /** Cumulative pointer travel this gesture; lets taps be told from drags. */
  dragDistance = 0;

  private tmpQ = new THREE.Quaternion();
  focus = new THREE.Vector3();
  vfx: Vfx;
  private warpTunnel: WarpTunnel;
  private lastFrame = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, logarithmicDepthBuffer: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.5, 6e7);

    this.scene.add(new THREE.AmbientLight(0x8899bb, 0.5));
    const sunLight = new THREE.DirectionalLight(0xfff0d8, 1.6);
    sunLight.position.set(0.4, 0.8, 0.3);
    this.scene.add(sunLight);

    this.vfx = new Vfx(this.scene);
    this.warpTunnel = new WarpTunnel(this.scene);
    this.resize();
    addEventListener('resize', () => this.resize());
    this.bindInput(canvas);
  }

  setSystemSkybox(systemId: number): void {
    this.scene.background = paintSkybox(0xd33b ^ systemId);
  }

  resize(): void {
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(innerWidth, innerHeight);
  }

  private bindInput(canvas: HTMLCanvasElement): void {
    // touch-action:none (set in CSS) + pointer capture keep the browser from
    // stealing the gesture after ~10px (the "drag stops after a short
    // distance" failure on mobile). Two pointers = pinch zoom.
    const pointers = new Map<number, { x: number; y: number }>();
    let pinchDist = 0;

    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      this.dragDistance = 0;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      }
      e.preventDefault();
    });

    const release = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);
    canvas.addEventListener('lostpointercapture', (e) => pointers.delete(e.pointerId));

    canvas.addEventListener('pointermove', (e) => {
      const p = pointers.get(e.pointerId);
      if (!p) return;
      if (pointers.size === 2) {
        // Pinch zoom
        p.x = e.clientX;
        p.y = e.clientY;
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchDist > 0 && d > 0) {
          this.camDist = Math.max(40, Math.min(60_000, this.camDist * (pinchDist / d)));
        }
        pinchDist = d;
      } else if (pointers.size === 1) {
        this.dragDistance += Math.hypot(e.clientX - p.x, e.clientY - p.y);
        this.camYaw -= (e.clientX - p.x) * 0.005;
        this.camPitch = Math.max(-1.4, Math.min(1.4, this.camPitch + (e.clientY - p.y) * 0.005));
        p.x = e.clientX;
        p.y = e.clientY;
      }
    });

    canvas.addEventListener(
      'wheel',
      (e) => {
        this.camDist = Math.max(40, Math.min(60_000, this.camDist * (e.deltaY > 0 ? 1.15 : 0.87)));
        e.preventDefault();
      },
      { passive: false },
    );
  }

  private buildFor(ballId: number, deco: BallDeco, ball: Ball): THREE.Group {
    switch (deco.kind) {
      case 'ship':
      case 'npc':
        return buildShip(deco.shipClass ?? 'dart', deco.faction ?? 'Waker');
      case 'station':
        return buildStation(deco.faction ?? 'Provenance Combine');
      case 'gate':
        return buildGate();
      case 'belt':
        return buildBeltBeacon();
      case 'asteroid':
        return buildAsteroid(ballId, ball.radius);
      case 'wreck':
        return buildWreck();
      case 'sun':
        return buildSun(Math.min(ball.radius, 2.0e6)); // impostor-scaled below anyway
      case 'planet-gas':
        return buildPlanet('gas', ballId, Math.min(ball.radius, 2.0e6));
      case 'planet-rock':
        return buildPlanet('rock', ballId, Math.min(ball.radius, 2.0e6));
      case 'planet-ice':
        return buildPlanet('ice', ballId, Math.min(ball.radius, 2.0e6));
      default: {
        const g = new THREE.Group();
        g.add(
          new THREE.Mesh(
            new THREE.SphereGeometry(Math.max(4, ball.radius), 10, 10),
            new THREE.MeshStandardMaterial({ color: 0x99aabb }),
          ),
        );
        return g;
      }
    }
  }

  /** Interpolated world position of a ball at render time. */
  static lerpPos(ball: Ball, alpha: number, out: THREE.Vector3): THREE.Vector3 {
    out.set(
      ball.oldPos.x + (ball.newPos.x - ball.oldPos.x) * alpha,
      ball.oldPos.y + (ball.newPos.y - ball.oldPos.y) * alpha,
      ball.oldPos.z + (ball.newPos.z - ball.oldPos.z) * alpha,
    );
    return out;
  }

  render(sim: ClientSim, now: number): void {
    const alpha = sim.alpha(now);
    const me = sim.myBall();
    if (me) Renderer.lerpPos(me, alpha, this.focus);

    const seen = new Set<number>();
    const tmp = new THREE.Vector3();

    for (const [id, ball] of sim.park.balls) {
      const deco = sim.deco.get(id);
      if (!deco) continue;
      seen.add(id);

      let entry = this.objects.get(id);
      const builtFor = `${deco.kind}|${deco.shipClass ?? ''}|${deco.faction ?? ''}`;
      if (!entry || entry.builtFor !== builtFor) {
        if (entry) this.scene.remove(entry.group);
        const group = this.buildFor(id, deco, ball);
        const blinkers = (group.userData.blinkers as THREE.Mesh[] | undefined) ?? [];
        const spinners: THREE.Object3D[] = [];
        const engines: THREE.Mesh[] = [];
        let gateDisc: THREE.Mesh | null = null;
        group.traverse((o) => {
          if (o.userData.spin) spinners.push(o);
          if (o.userData.engine) engines.push(o as THREE.Mesh);
          if (o.userData.gateDisc) gateDisc = o as THREE.Mesh;
        });
        entry = { group, kind: deco.kind, builtFor, blinkers, spinners, gateDisc, engines };
        this.scene.add(group);
        this.objects.set(id, entry);
      }

      Renderer.lerpPos(ball, alpha, tmp).sub(this.focus);
      const dist = tmp.length();
      if (dist > IMPOSTOR_RANGE) {
        // Collapse far objects onto the impostor sphere, scaled down
        const k = IMPOSTOR_RANGE / dist;
        tmp.multiplyScalar(k);
        const s = Math.max(k, deco.kind === 'sun' ? k * 6 : 0.05);
        entry.group.scale.setScalar(s);
      } else {
        entry.group.scale.setScalar(1);
      }
      entry.group.position.copy(tmp);

      // Orientation: yaw/pitch/roll from the sim (ships only)
      if (deco.kind === 'ship' || deco.kind === 'npc') {
        const yaw = ball.oldYaw + (ball.newYaw - ball.oldYaw) * alpha;
        const pitch = ball.oldPitch + (ball.newPitch - ball.oldPitch) * alpha;
        const roll = ball.oldRoll + (ball.newRoll - ball.oldRoll) * alpha;
        entry.group.rotation.set(pitch, yaw, roll, 'YXZ');
        // Engine glow breathes with thrust
        const speed = ball.newVel.length();
        const frac = ball.maxVel > 0 ? Math.min(1.6, speed / ball.maxVel) : 0;
        const pulse = 0.35 + frac * 0.9 + Math.sin(now * 0.02 + id) * 0.06 * frac;
        for (const eng of entry.engines) {
          eng.scale.set(pulse, pulse, pulse * (0.45 + frac * 0.9));
        }
      } else if (deco.kind === 'gate' || deco.kind === 'belt' || deco.kind === 'asteroid') {
        entry.group.rotation.y += deco.kind === 'asteroid' ? 0.0011 : 0.0004; // idle drift
        if (deco.kind === 'gate') {
          entry.group.rotation.z += 0.00025; // the waking gate breathes
          if (entry.gateDisc) {
            (entry.gateDisc.material as THREE.MeshBasicMaterial).opacity =
              0.1 + 0.1 * (0.5 + 0.5 * Math.sin(now * 0.0011 + id));
          }
        }
      }
      // Generic part animation: nav-light blinks and slow spins
      for (const b of entry.blinkers) {
        const phase = (b.userData.blinkPhase as number) ?? 0;
        const mat = b.material as THREE.MeshBasicMaterial;
        mat.opacity = Math.sin(now * 0.004 + phase * Math.PI * 2) > 0.2 ? 0.95 : 0.15;
      }
      for (const sp of entry.spinners) sp.rotation.y += sp.userData.spin as number;
    }

    // Remove stale objects
    for (const [id, entry] of this.objects) {
      if (!seen.has(id)) {
        this.scene.remove(entry.group);
        this.objects.delete(id);
      }
    }

    // Effects
    const dt = this.lastFrame ? Math.min(0.1, (now - this.lastFrame) / 1000) : 0.016;
    this.lastFrame = now;
    this.vfx.update(sim, now, this.focus);
    {
      const meBall = sim.myBall();
      const warping = !!meBall && meBall.isWarping();
      const speed = meBall ? meBall.newVel.length() : 0;
      const heading = meBall
        ? new THREE.Vector3(meBall.newVel.x, meBall.newVel.y, meBall.newVel.z)
        : new THREE.Vector3();
      this.warpTunnel.update(warping, speed, heading, dt);
    }

    // Camera: orbit the focus
    const cp = this.camPitch;
    const cy = this.camYaw;
    this.camera.position.set(
      Math.cos(cp) * Math.sin(cy) * this.camDist,
      Math.sin(cp) * this.camDist,
      Math.cos(cp) * Math.cos(cy) * this.camDist,
    );
    this.camera.lookAt(0, 0, 0);

    this.renderer.render(this.scene, this.camera);
  }

  /** Screen-space pick: nearest ball to the pointer within 24px. */
  pick(sim: ClientSim, px: number, py: number, now: number): number | null {
    const alpha = sim.alpha(now);
    const me = sim.myBall();
    const focus = new THREE.Vector3();
    if (me) Renderer.lerpPos(me, alpha, focus);
    const tmp = new THREE.Vector3();
    let best: number | null = null;
    let bestD = 24;
    for (const [id, ball] of sim.park.balls) {
      if (id === sim.myBallId) continue;
      Renderer.lerpPos(ball, alpha, tmp).sub(focus);
      const d = tmp.length();
      if (d > IMPOSTOR_RANGE) tmp.multiplyScalar(IMPOSTOR_RANGE / d);
      tmp.project(this.camera);
      if (tmp.z > 1) continue;
      const sx = (tmp.x * 0.5 + 0.5) * innerWidth;
      const sy = (-tmp.y * 0.5 + 0.5) * innerHeight;
      const pd = Math.hypot(sx - px, sy - py);
      if (pd < bestD) {
        bestD = pd;
        best = id;
      }
    }
    return best;
  }
}
